import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Radar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errMsg } from "@/lib/api";

const RADARS = [["engineering_radar", "Engineering hiring radar"], ["funding_radar", "Funding radar"], ["leadership_radar", "Leadership change radar"], ["workforce_radar", "Workforce radar"], ["job_posting_intel", "Job posting intel"]];
const isRadar = (w) => RADARS.some(([k]) => k === w.kind);
const result = (d) => d?.message || (typeof d?.last_result === "string" ? d.last_result : d?.last_result?.summary) || "";

// Runs the scheduled radar workflows now; creates the two core radars if none exist yet.
export const RunRadar = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/rcc/workflows").then((r) => setList((r.data?.items || []).filter(isRadar))).catch((e) => setList(e.response?.status === 404 ? "missing" : []));
  useEffect(() => { load(); }, []);
  const run = async (ws) => {
    setBusy(true);
    try {
      let targets = ws;
      if (!targets.length) {
        targets = await Promise.all(RADARS.slice(0, 2).map(([kind, name]) => api.post("/admin/rcc/workflows", { name, kind, schedule: "daily 07:00", enabled: true }).then((r) => r.data?.item || r.data)));
        toast.message("Created Engineering and Funding radars (daily 7am).");
      }
      const out = await Promise.all(targets.map((w) => api.post(`/admin/rcc/workflows/${w.id}/run`, {}).then((r) => [w.name, result(r.data)]).catch((e) => [w.name, `failed: ${errMsg(e)}`])));
      toast.success(`Radar started. New signals land in Opportunity Radar as they're found.${out.map(([n, r]) => (r ? `\n${n}: ${r}` : "")).join("")}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (list === "missing") return <span className="text-[11px] text-zinc-500" data-testid="run-radar-missing">Radars aren't available on the backend yet.</span>;
  const icon = busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radar className="h-3.5 w-3.5" />;
  if (!list || list.length < 2) return <Button size="sm" onClick={() => run(list || [])} disabled={busy || !list} className="h-8 bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="run-radar">{icon}Run radar now</Button>;
  return (
    <DropdownMenu><DropdownMenuTrigger asChild><Button size="sm" disabled={busy} className="h-8 bg-[#D4AF37] text-black hover:bg-[#F3C649]" data-testid="run-radar">{icon}Run radar now</Button></DropdownMenuTrigger>
      <DropdownMenuContent className="border-[#27272A] bg-[#0f0f12]">
        <DropdownMenuItem onClick={() => run(list)} data-testid="run-radar-all">Run all {list.length} radars</DropdownMenuItem>
        {list.map((w) => <DropdownMenuItem key={w.id} onClick={() => run([w])} data-testid={`run-radar-${w.id}`}>{w.name}{w.last_run_at ? ` · last ${new Date(w.last_run_at).toLocaleDateString()}` : " · never run"}</DropdownMenuItem>)}
      </DropdownMenuContent></DropdownMenu>
  );
};
